import PropTypes from 'prop-types';
import Modal from 'components/ui-kit/modal';
import Button from 'components/ui-kit/buttons/button';
import { useNavigate } from 'react-router-dom';
import {
  useApp,
  useFields,
  useModal,
  useToast,
} from 'hooks';
import { Field, Select } from 'components/ui-kit/form';
import { buildCardSelectEntry } from 'helpers/business.helper';
import { calculateCcSurcharge, creditWithTip } from 'helpers/calculations.helper';
import {
  DEFAULT_PRESET_PERCENTAGES,
  createEmptyTipData,
  getSurchargeName,
  getTipPercentageSuffix,
} from 'helpers/payment-display.helper';

import { useState } from 'react';
import Typography from 'components/ui-kit/typography';
import Money from 'components/ui-kit/money';
import TipSelector from './components/tip-selector';
import * as S from './styles';

const InvoicePaymentOptionModal = ({
  cards,
  invoiceId,
  totalAmount,
  tipBaseAmount,
  invoiceSettingsOverride,
  creditApplied: creditBeforeTip,
  tipData: initialTipData,
  isTippingEnabled,
  presetPercentages,
  payWithSavedCardAction,
  redirectUrl,
  deposit,
}) => {
  const { portal, country } = useApp();
  const navigate = useNavigate();
  const { fields, handleClickChange } = useFields({
    paymentMethodId: cards[0]?.methodId || '',
  });
  const [isLoading, setIsLoading] = useState(false);
  const { addErrorToast } = useToast();
  const [tipData, setTipData] = useState(initialTipData || createEmptyTipData());
  const [isTipValid, setIsTipValid] = useState(true);
  const { hideModal } = useModal();
  const creditApplied = creditWithTip(creditBeforeTip, totalAmount, tipData.tipAmount, country);

  const cardOptions = cards.map((card) => ({
    value: card.methodId,
    label: buildCardSelectEntry(card),
  }));

  const getEffectiveCcSurcharge = () => {
    const override = invoiceSettingsOverride?.ccSurchargeOverride?.enabled;
    const globalCcSurcharge = portal?.paymentSettings?.ccSurchargeSettings;

    if (override === false) {
      return null;
    }

    if (override === true) {
      return globalCcSurcharge;
    }

    const globalEnabled = globalCcSurcharge?.isEnabled && globalCcSurcharge?.value > 0;

    return globalEnabled ? globalCcSurcharge : null;
  };

  const effectiveCcSurcharge = getEffectiveCcSurcharge();
  const isCcSurchargeEnabled = effectiveCcSurcharge !== null;
  // Net of credit: once credit covers the balance, nothing is actually charged to the card (the backend
  // skips Stripe entirely), so a flat surcharge shouldn't preview as still being added, and a percentage
  // one shouldn't preview off the pre-credit total either - matches how the real charge is computed.
  const surchargeBaseAmount = Math.max(totalAmount + (tipData.tipAmount || 0) - creditApplied, 0);

  const surchargeAmount = isCcSurchargeEnabled && !deposit && surchargeBaseAmount > 0
    ? calculateCcSurcharge(effectiveCcSurcharge, surchargeBaseAmount)
    : 0;

  const redirectToInvoice = () => {
    navigate(redirectUrl);
  };

  const handlePayment = async () => {
    try {
      setIsLoading(true);
      const { paymentMethodId } = fields;

      const { data: { errorMessage, isSuccess, skippedPayment } } = await payWithSavedCardAction(
        portal.id,
        {
          paymentMethodId,
          invoiceId,
          tipAmount: tipData.tipAmount,
          tipType: tipData.tipType,
          tipPercentage: tipData.tipPercentage,
          deposit,
        },
      );

      if (skippedPayment) {
        redirectToInvoice();
        return;
      }

      if (isSuccess) {
        redirectToInvoice();
      } else {
        addErrorToast(errorMessage || 'Something went wrong');
      }
    } finally {
      setIsLoading(false);
      hideModal();
    }
  };

  return (
    <Modal title="Select payment option" isClosable={false}>
      <S.Box>
        <Field
          name="paymentMethodId"
          label="Card"
          value={fields.paymentMethodId}
          onChange={handleClickChange}
          options={cardOptions}
          element={Select}
          fullWidth
        />

        {isTippingEnabled && (
          <TipSelector
            totalAmount={totalAmount}
            tipBaseAmount={tipBaseAmount}
            creditApplied={creditBeforeTip}
            onTipChange={setTipData}
            onTipValidationChange={setIsTipValid}
            presetPercentages={presetPercentages}
          />
        )}

        {!isTippingEnabled && tipData.tipAmount > 0 && (
          <S.TipSummary>
            <Typography fontSize={13} color="grey" fontFamily="regular">
              Tip selected:
              {' '}
              <S.SummaryAmount>
                <Money amount={tipData.tipAmount} />
              </S.SummaryAmount>
              {getTipPercentageSuffix(tipData.tipPercentage)}
            </Typography>
            <Typography fontSize={13} color="grey" fontFamily="regular">
              Amount to pay:
              {' '}
              <S.SummaryAmount>
                <Money amount={totalAmount + surchargeAmount + tipData.tipAmount - creditApplied} />
              </S.SummaryAmount>
            </Typography>
          </S.TipSummary>
        )}

        {creditApplied > 0 && (
          <S.CcSurchargeNotice>
            <Typography fontSize={14} color="grey">
              <Money amount={-creditApplied} />
              {' '}
              credit will be applied, reducing your total
            </Typography>
          </S.CcSurchargeNotice>
        )}

        {surchargeAmount > 0 && (
          <S.CcSurchargeNotice>
            <Typography fontSize={14} color="grey">
              +
              {' '}
              <Money amount={surchargeAmount} />
              {' '}
              {getSurchargeName(effectiveCcSurcharge, 'Credit Card Surcharge')}
              {' '}
              will be added
            </Typography>
          </S.CcSurchargeNotice>
        )}

        <S.ButtonGroup>
          <Button isLoading={isLoading} onClick={hideModal} stretched>
            Cancel
          </Button>
          <Button
            isLoading={isLoading}
            color="primary"
            onClick={handlePayment}
            disabled={!isTipValid}
            stretched
          >
            Proceed
          </Button>
        </S.ButtonGroup>
      </S.Box>
    </Modal>
  );
};

InvoicePaymentOptionModal.propTypes = {
  invoiceId: PropTypes.string.isRequired,
  totalAmount: PropTypes.number.isRequired,
  tipBaseAmount: PropTypes.number,
  cards: PropTypes.arrayOf(
    PropTypes.shape({
      methodId: PropTypes.string.isRequired,
    }),
  ),
  invoiceSettingsOverride: PropTypes.shape({
    ccSurchargeOverride: PropTypes.shape({
      enabled: PropTypes.bool.isRequired,
    }),
  }),
  creditApplied: PropTypes.number,
  tipData: PropTypes.shape({
    tipAmount: PropTypes.number,
    tipType: PropTypes.number,
    tipPercentage: PropTypes.number,
  }),
  isTippingEnabled: PropTypes.bool,
  presetPercentages: PropTypes.arrayOf(PropTypes.number),
  payWithSavedCardAction: PropTypes.func.isRequired,
  redirectUrl: PropTypes.string.isRequired,
  deposit: PropTypes.bool,
};

InvoicePaymentOptionModal.defaultProps = {
  tipBaseAmount: null,
  cards: [],
  invoiceSettingsOverride: null,
  creditApplied: 0,
  tipData: createEmptyTipData(),
  isTippingEnabled: false,
  presetPercentages: DEFAULT_PRESET_PERCENTAGES,
  deposit: false,
};

export default InvoicePaymentOptionModal;
